/* Native variant selection owns variant IDs; these cards only forward that state. */
if (!customElements.get('ring-stone-choices')) {
  customElements.define('ring-stone-choices', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const signal = this.controller.signal;
      const form = document.getElementById(this.getAttribute('form-id'));
      this.sync = (variant) => {
        this.querySelectorAll('[data-stone-link]').forEach(link => {
          const url = new URL(link.href);
          if (variant?.id) url.searchParams.set('settingVariantId', variant.id);
          link.href = url.toString();
          const available = Boolean(variant?.id && variant.available);
          link.setAttribute('aria-disabled', String(!available));
          if (available) link.removeAttribute('tabindex'); else link.tabIndex = -1;
        });
      };
      form?.addEventListener('variant:change', event => this.sync(event.detail.variant), { signal });
      this.addEventListener('click', event => {
        const link = event.target.closest('[data-stone-link]');
        if (link?.getAttribute('aria-disabled') === 'true') event.preventDefault();
      }, { signal });
      /* Do not continue while Shopify is resolving a newly chosen combination. */
      document.addEventListener('change', event => {
        if (event.target.matches('input[data-option-position]') && event.target.form === form) {
          this.querySelectorAll('[data-stone-link]').forEach(link => {link.setAttribute('aria-disabled', 'true');link.tabIndex = -1;});
        }
      }, { signal });
    }
    disconnectedCallback() { this.controller?.abort(); }
  });
}

/* Keep this dialog within its section during opening and variant rerenders. */
customElements.whenDefined('x-modal').then(() => {
  if (!customElements.get('ring-metal-dialog')) {
    customElements.define('ring-metal-dialog', class extends customElements.get('x-modal') {
      get shouldAppendToBody() { return false; }
    });
  }
});

if (!customElements.get('ring-thumb-slider')) {
  customElements.define('ring-thumb-slider', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const signal = this.controller.signal;
      const scroller = this.querySelector('product-gallery-navigation');
      const prev = this.querySelector('[data-thumb-prev]');
      const next = this.querySelector('[data-thumb-next]');
      const update = () => {
        const position = Math.abs(scroller.scrollLeft);
        prev.disabled = position < 2;
        next.disabled = position >= scroller.scrollWidth - scroller.clientWidth - 2;
      };
      const move = direction => scroller.scrollBy({left: direction * (scroller.clientWidth + 8), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
      prev.addEventListener('click', () => move(-1), {signal});
      next.addEventListener('click', () => move(1), {signal});
      scroller.addEventListener('scroll', update, {signal, passive:true});
      this.observer = new ResizeObserver(update);
      this.observer.observe(scroller);
      this.mutations = new MutationObserver(update);
      this.mutations.observe(scroller, {subtree:true, attributes:true, attributeFilter:['hidden']});
      update();
    }
    disconnectedCallback() {this.controller?.abort(); this.observer?.disconnect(); this.mutations?.disconnect();}
  });
}
/* Reuse merchant-authored description sections without changing product data. */
if (!customElements.get('ring-product-tabs')) {
  customElements.define('ring-product-tabs', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      const list = this.querySelector('[data-tab-list]');
      if (!list) return;
      this.importDescription(list);
      const entries = [...list.querySelectorAll('[data-tab]')].map(tab => ({
        tab, panel: this.querySelector(`[id="${tab.dataset.tab}"]`)
      })).filter(({panel}) => panel);
      if (!entries.length) return;
      const current = Math.max(0, entries.findIndex(({tab}) => tab.getAttribute('aria-selected') === 'true'));
      this.select = index => entries.forEach(({tab,panel}, i) => {
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', panel.id);
        tab.setAttribute('aria-selected', String(i === index));
        tab.tabIndex = i === index ? 0 : -1;
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', tab.id);
        panel.tabIndex = 0;
        panel.hidden = i !== index;
        const title = panel.querySelector('[data-fallback-title]');
        if (title) title.hidden = true;
      });
      entries.forEach(({tab}, index) => {
        tab.addEventListener('click', () => this.select(index), {signal});
        tab.addEventListener('keydown', event => {
          let target;
          const rtl = getComputedStyle(list).direction === 'rtl';
          if (event.key === 'ArrowRight') target = (index + (rtl ? -1 : 1) + entries.length) % entries.length;
          if (event.key === 'ArrowLeft') target = (index + (rtl ? 1 : -1) + entries.length) % entries.length;
          if (event.key === 'Home') target = 0;
          if (event.key === 'End') target = entries.length - 1;
          if (target !== undefined) {
            event.preventDefault();
            this.select(target);
            entries[target].tab.focus({preventScroll:true});
            entries[target].tab.scrollIntoView({block:'nearest',inline:'nearest'});
          }
        }, {signal});
      });
      this.addEventListener('shopify:block:select', event => {
        const index = entries.findIndex(({panel}) => panel.id.endsWith(`-${event.detail.blockId}`));
        if (index !== -1) this.select(index);
      }, {signal});
      this.select(current);
      list.setAttribute('role', 'tablist');
      list.hidden = false;
    }
    importDescription(list) {
      const source = this.querySelector('[data-product-description]');
      if (!source || source.hasAttribute('data-migrated')) return;
      const key = text => text.toLowerCase().replace(/&/g,' and ').replace(/[^\p{L}\p{N}]+/gu,' ').trim();
      const panels = () => [...this.querySelectorAll(':scope > [data-tab-panel]')];
      source.querySelectorAll('style,script').forEach(node => node.remove());
      let count = 0;
      for (const original of [...source.querySelectorAll('.accordion-panel,.accordion-content')]) {
        if (original.parentElement.closest('.accordion-panel,.accordion-content')) continue;
        const heading = original.previousElementSibling;
        if (!heading?.matches('h1,h2,h3,h4,h5,h6,.accordion-header,.accordion-title')) continue;
        const headingCopy = heading.cloneNode(true);
        headingCopy.querySelectorAll('.accordion-indicator,[aria-hidden="true"]').forEach(node => node.remove());
        const title = headingCopy.textContent.replace(/[+−–]\s*$/,'').trim();
        if (!title) continue;
        const body = original.querySelector(':scope > .accordion-panel-inner') || original;
        let panel = panels().find(node => key(node.dataset.detailHeading || '') === key(title));
        let tab = panel && [...list.querySelectorAll('[data-tab]')].find(node => node.dataset.tab === panel.id);
        const hasContent = Boolean(body.textContent.trim() || body.querySelector('img,video,table,iframe'));
        if (hasContent) {
          if (!panel) {
            panel = document.createElement('div');
            panel.className = 'cr-tab-panel prose';
            panel.id = `panel-${this.dataset.sectionId}-original-${++count}`;
            panel.setAttribute('data-tab-panel','');
            panel.dataset.detailHeading = title;
            tab = document.createElement('button');
            tab.type = 'button';
            tab.className = 'cr-tab';
            tab.id = `tab-${this.dataset.sectionId}-original-${count}`;
            tab.dataset.tab = panel.id;
            // Keep delivery and returns at the end of the information menu.
            const processing = panels().find(node => key(node.dataset.detailHeading || '').startsWith('processing'));
            const processingTab = processing && [...list.children].find(node => node.dataset.tab === processing.id);
            list.insertBefore(tab, processingTab || null);
            this.insertBefore(panel, processing || null);
          }
          const fallback = document.createElement('h2');
          fallback.setAttribute('data-fallback-title','');
          fallback.textContent = title;
          panel.replaceChildren(fallback, ...body.childNodes);
          // Merchant labels stay editable; the original heading maps the source content.
          if (!tab.textContent.trim()) tab.textContent = title;
        } else {
          // An intentionally empty product section must not expose generic fallback copy.
          tab?.remove();
          panel?.remove();
        }
        const wrapper = original.parentElement;
        heading.remove();
        original.remove();
        if (wrapper.matches('.accordion-section') && !wrapper.textContent.trim()) wrapper.remove();
      }
      // Apply the merchant-approved production range while preserving the surrounding copy and links.
      if (this.dataset.productionTime) {
        for (const panel of panels().filter(node => key(node.dataset.detailHeading || '').startsWith('processing'))) {
          const walker = document.createTreeWalker(panel, NodeFilter.SHOW_TEXT);
          while (walker.nextNode()) {
            walker.currentNode.textContent = walker.currentNode.textContent.replace(/\b\d+\s*(?:to|[-–—])\s*\d+\s*weeks\b/gi, this.dataset.productionTime);
          }
        }
      }
      source.setAttribute('data-migrated','');
    }
    disconnectedCallback() { this.controller?.abort(); }
  });
}
